/**
 * Filters for the signals list.
 *
 * Unlike the other facet sets this one carries no counts. The list is
 * already the narrow set — companies that trip at least one signal — and
 * a count beside each box would mean a second aggregation over the whole
 * graph on every keystroke to tell a reader something the row count
 * already tells them. `toFilter` therefore returns the same arguments as
 * `toWhere`: the resolver takes scalars, not a generated `where`.
 */

import type { FacetSet } from './types';

export interface SignalState extends Record<string, unknown> {
  search: string;
  /** Signal keys the API knows: singleBid | directAward | … */
  flags: string[];
  /** The party-standing predicates, kept apart in the sidebar and sent
      together with the rest — the resolver takes one list. */
  partyFlags: string[];
  /** Award year, or '' for every year at once. */
  year: string;
  share: string;
  band: string;
}

export interface SignalArgs extends Record<string, unknown> {
  search: string;
  flags: string[];
  year: number;
  minShare: number;
  minFlaggedValue: number;
}

export const SIGNAL_FLAGS = [
  { value: 'singleBid', label: 'единствена оферта' },
  { value: 'directAward', label: 'директно възлагане' },
  { value: 'subcontracted', label: 'с подизпълнители' },
  { value: 'cobidders', label: 'споделят лице и възложител' },
  { value: 'soleSupplier', label: 'единствен изпълнител на възложител' },
] as const;

/* The two halves of local power, as filters. Each of these is one of the
   figures printed on a card above the table, and a card that reports a
   number the reader cannot open is a number they have to take on trust. */
const PARTY_FLAGS = [
  // A lead rather than a finding, and the label says so: the identity is
  // never established, only a name and a place that coincide.
  { value: 'councilInterest', label: 'име на съветник сред собствениците (за проверка)' },
  { value: 'onePartyMayors', label: 'под кметове на една партия (3+ възложителя)' },
  { value: 'onePartyCouncils', label: 'под съвети, водени от една партия' },
  { value: 'cohabitation', label: 'кметът и съветът са от различни партии' },
] as const;

const SHARES = [
  { value: '0.25', label: 'над 25%' },
  { value: '0.5', label: 'над 50%' },
  { value: '0.9', label: 'над 90%' },
] as const;

/* The bands the money actually falls into. Flagged value spans seven
   orders of magnitude, so linear steps would put nearly every company in
   one bucket and leave the interesting end empty. */
const BANDS = [
  { value: '100000', label: 'над 100 хил. лв', min: 100_000 },
  { value: '1000000', label: 'над 1 млн. лв', min: 1_000_000 },
  { value: '10000000', label: 'над 10 млн. лв', min: 10_000_000 },
  { value: '100000000', label: 'над 100 млн. лв', min: 100_000_000 },
] as const;

export function signalFacetSet(years: readonly number[]): FacetSet<SignalState, SignalArgs, null> {
  return {
    prefix: 'g.',
    groups: [
      {
        key: 'flags',
        title: 'Признаци',
        kind: 'check',
        // Every box narrows: two ticked means both, not either. Same rule
        // the tender indicators follow, so a reader who has used one list
        // is not surprised by the other.
        options: SIGNAL_FLAGS.map((flag) => ({ value: flag.value, label: flag.label })),
      },
      {
        // Its own state key, merged back into `flags` for the query: two
        // sidebar groups cannot share one, because the panel keys its
        // sections and its fold state on it.
        key: 'partyFlags',
        title: 'Партийна принадлежност',
        kind: 'check',
        options: PARTY_FLAGS.map((flag) => ({ value: flag.value, label: flag.label })),
      },
      {
        key: 'year',
        title: 'Година на възлагане',
        kind: 'radio',
        all: true,
        // Offered from the archive rather than hard-coded, so the filter
        // cannot outlive the data.
        options: years.map((year) => ({ value: String(year), label: String(year) })),
      },
      { key: 'share', title: 'Дял от възложител', kind: 'radio', all: true, options: SHARES },
      { key: 'band', title: 'Стойност с признак', kind: 'radio', all: true, options: BANDS },
    ],

    emptyState: () => ({ search: '', flags: [], partyFlags: [], year: '', share: '', band: '' }),

    fromParams: (get, getAll) => ({
      search: get('q') ?? '',
      flags: getAll('flags'),
      partyFlags: getAll('party'),
      year: get('year') ?? '',
      share: get('share') ?? '',
      band: get('band') ?? '',
    }),

    toParams: (state) => ({
      q: state.search || null,
      flags: state.flags.length ? state.flags : null,
      party: state.partyFlags.length ? state.partyFlags : null,
      year: state.year || null,
      share: state.share || null,
      band: state.band || null,
    }),

    activeCount: (state) => state.flags.length + state.partyFlags.length
      + (state.search ? 1 : 0) + (state.year ? 1 : 0)
      + (state.share ? 1 : 0) + (state.band ? 1 : 0),

    toFilter: (state) => ({
      search: state.search,
      flags: [...state.flags, ...state.partyFlags],
      year: Number(state.year) || 0,
      minShare: Number(state.share) || 0,
      minFlaggedValue: Number(state.band) || 0,
    }),

    // The resolver takes scalars; there is no generated `where` to build.
    toWhere: () => null,
  };
}
